import React, { useEffect, useRef, useState } from "react"
import Hammer from "hammerjs"

type PropType = {
  largeImage: string
  alt: string
  minScale?: number
  maxScale?: number
  objectFit?: "contain" | "cover"
}

const ZoomAndPan: React.FC<PropType> = ({
  largeImage,
  alt,
  minScale = 1.5,
  maxScale = 4,
  objectFit = "cover"
}) => {
  const zoomContainerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [scale, setScale] = useState(minScale)
  const positionRef = useRef({ x: 0, y: 0 })
  const scaleRef = useRef(minScale)
  const initialPanPositionRef = useRef({ x: 0, y: 0 })
  const initialScaleRef = useRef(minScale)

  useEffect(() => {
    // Initial centering of the image
    const centerImage = () => {
      if (zoomContainerRef.current && imageRef.current) {
        const containerWidth = zoomContainerRef.current.clientWidth
        const containerHeight = zoomContainerRef.current.clientHeight
        const imageWidth = imageRef.current.clientWidth
        const imageHeight = imageRef.current.clientHeight

        const initialX = (containerWidth - imageWidth) / 2
        const initialY = (containerHeight - imageHeight) / 2

        setPosition({ x: initialX, y: initialY })
        positionRef.current = { x: initialX, y: initialY }
        imageRef.current.style.transform = `translate(${initialX}px, ${initialY}px) scale(${scaleRef.current})`
      }
    }

    // apply boundaries to the image so it doesn't go out of the container while panning.
    const applyBoundaries = (x: number, y: number, scale: number) => {
      if (!zoomContainerRef.current || !imageRef.current) return { x, y }

      const containerWidth = zoomContainerRef.current.clientWidth
      const containerHeight = zoomContainerRef.current.clientHeight
      const imageWidth = imageRef.current.clientWidth * scale
      const imageHeight = imageRef.current.clientHeight * scale

      let minX = -(imageWidth - containerWidth) / 2
      let maxX = (imageWidth - containerWidth) / 2
      let minY = (containerHeight - imageHeight) / 2
      let maxY = -(containerHeight - imageHeight) / 2

      const boundedX = Math.max(minX, Math.min(maxX, x))
      const boundedY = Math.max(minY, Math.min(maxY, y))

      return { x: boundedX, y: boundedY }
    }

    if (imageRef.current) {
      if (imageRef.current.complete) {
        centerImage()
      } else {
        imageRef.current.onload = centerImage
      }

      // Hammer.js instantiation and event handling
      const hammer = new Hammer(imageRef.current, {
        inputClass: Hammer.TouchMouseInput
      })

      hammer.get("pan").set({ direction: Hammer.DIRECTION_ALL })
      hammer.get("pinch").set({ enable: true })

      hammer.on("panstart", () => {
        initialPanPositionRef.current = {
          x: positionRef.current.x,
          y: positionRef.current.y
        }
      })

      hammer.on("pan", (ev) => {
        let newPosX = initialPanPositionRef.current.x + ev.deltaX
        let newPosY = initialPanPositionRef.current.y + ev.deltaY

        const boundedPos = applyBoundaries(newPosX, newPosY, scaleRef.current)
        newPosX = boundedPos.x
        newPosY = boundedPos.y

        if (imageRef.current) {
          imageRef.current.style.transform = `translate(${newPosX}px, ${newPosY}px) scale(${scaleRef.current})`
        }
      })

      hammer.on("panend", (ev) => {
        let finalPosX = initialPanPositionRef.current.x + ev.deltaX
        let finalPosY = initialPanPositionRef.current.y + ev.deltaY

        const boundedPos = applyBoundaries(
          finalPosX,
          finalPosY,
          scaleRef.current
        )
        finalPosX = boundedPos.x
        finalPosY = boundedPos.y

        positionRef.current = { x: finalPosX, y: finalPosY }
        setPosition({ x: finalPosX, y: finalPosY })
      })

      hammer.on("pinchstart", () => {
        initialScaleRef.current = scaleRef.current
      })

      hammer.on("pinch", (ev) => {
        let newScale = initialScaleRef.current * ev.scale

        if (newScale < minScale) newScale = minScale
        if (newScale > maxScale) newScale = maxScale

        scaleRef.current = newScale
        setScale(newScale)

        const boundedPos = applyBoundaries(
          positionRef.current.x,
          positionRef.current.y,
          newScale
        )
        const newPosX = boundedPos.x
        const newPosY = boundedPos.y

        positionRef.current = { x: newPosX, y: newPosY }
        setPosition({ x: newPosX, y: newPosY })

        if (imageRef.current) {
          imageRef.current.style.transform = `translate(${newPosX}px, ${newPosY}px) scale(${newScale})`
        }
      })
    }

    window.addEventListener("resize", centerImage)

    return () => {
      window.removeEventListener("resize", centerImage)
    }
  }, [])

  return (
    <div
      ref={zoomContainerRef}
      style={{
        overflow: "hidden",
        width: "100%",
        height: "100%",
        position: "relative"
      }}
    >
      <img
        ref={imageRef}
        src={largeImage}
        alt={`${alt} - zoomed in`}
        style={{
          width: "100%",
          height: "100%",
          objectFit: objectFit,
          transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          transformOrigin: "center center",
          touchAction: "none"
        }}
      />
    </div>
  )
}

export default ZoomAndPan
